import { forwardRef, type ImgHTMLAttributes } from 'react';
import type { ResponsiveImageModel } from 'autogen/swagger/all';
import type { PictureProps } from 'components/generic/picture/shared/types';
import ImageBanner from 'components/pages/Contentful/components/Image/ImageBanner';
import type { IResponsiveImageModel } from 'shared/interfaces/responsiveImage';
import type { TypeImageBannerDetailed } from 'types/autogen/contentful';
import Picture from '../picture/Picture';
import PictureDeprecated from '../picture/PictureDeprecated';
import Image from './Image';
import type { AspectRatioValue, ImageProps } from './shared/types';

export type DynamicImageRendererModel =
    | TypeImageBannerDetailed
    | PictureProps
    | IResponsiveImageModel
    | ResponsiveImageModel
    | ImageProps
    | undefined;

const isContentfulType = (props: any): props is TypeImageBannerDetailed => 'sys' in props;
const isPictureProps = (props: any): props is PictureProps => 'transformType' in props || 'imageType' in props;
const isResponsiveImageModel = (props: any): props is ResponsiveImageModel => 'altText' in props && 'sources' in props;
const isImageProps = (props: any): props is ImageProps => 'altText' in props;

/*
 * This component is to handle the different models given by the new and old CMS.
 * Contenful will use the Asset model and the Old CMS will use ResponsiveImageModel
 */
const DynamicImageRenderer = forwardRef<
    HTMLImageElement,
    DynamicImageRendererModel & { aspectRatio?: AspectRatioValue } & ImgHTMLAttributes<HTMLImageElement>
>((props, ref) => {
    if (!props) return null;

    if (isContentfulType(props)) {
        return <ImageBanner {...props} />;
    }

    if (isPictureProps(props)) {
        return <Picture ref={ref} {...props} />;
    }

    if (isResponsiveImageModel(props)) {
        return <PictureDeprecated ref={ref} {...props} />;
    }

    if (isImageProps(props)) {
        return <Image ref={ref} {...props} />;
    }

    console.error('Image error: Unrecognized props', props);
    return null;
});

export default DynamicImageRenderer;
